/**
 * Simulation DAG executor (doc 147 §6.3 + v2 enrichment spec §3–§12).
 *
 * Walks the manifest to answer "if the user creates a Goal, what entities
 * get written, what events fire, what Cloud Functions run, in what order?"
 * Pure over (manifest, action) — the SequenceDiagram view renders the
 * resulting trace.
 *
 * Edge-cases handled:
 *   - Recursive CF chains terminate via visited-set on (cfName, entityId) +
 *     hop cap
 *   - CFs whose reads happen to overlap the freshly-written entities are
 *     treated as event-driven triggers (matches Firestore-trigger semantics)
 */
import type { AtlasManifest } from '@atlas-manifest/types';

// ─── v2 primitive types ───────────────────────────────────────────────────────

/** Cloud Function execution kind. */
export type CfKind = 'trigger' | 'callable' | 'scheduled' | 'http';

/** Agent safety tier from constitutional gate evaluation. */
export type SafetyTier = 'ALLOW' | 'WARN' | 'BLOCK';

/** p50 / p95 / p99 latency estimates in milliseconds. */
export interface LatencyBand {
  p50: number;
  p95: number;
  p99: number;
}

/** Condition that branches the happy path into an error / alternate path. */
export type BranchReason =
  | 'quota_exceeded'
  | 'auth_fail'
  | 'validation_error'
  | 'cf_error'
  | 'custom';

export interface BranchCondition {
  id: string;
  label: string;
  reason: BranchReason;
  /** Probability this branch occurs, 0–1. */
  probability: number;
}

/** Result of evaluating the 14-principle constitutional guard for one step. */
export interface AgentGateResult {
  tier: SafetyTier;
  reason: string;
  /** Principle IDs that fired, e.g. ['P3', 'P7']. */
  principleViolations: string[];
}

// ─── v2 scenario action (supersedes UserAction) ───────────────────────────────

/**
 * ScenarioAction is the v2 canonical action descriptor.
 * UserAction is kept as a type alias for backward compatibility.
 */
export interface ScenarioAction {
  id: string;
  label: string;
  /** Entities the action directly writes. */
  writes: string[];
  /** Events the action emits at hop 0. */
  fires: string[];
  /** Autonomy level required (0 = user-initiated, 3 = fully agentic). */
  autonomyRequired?: 0 | 1 | 2 | 3;
  /** Conditional branch paths from this action. */
  branchConditions?: BranchCondition[];
  /** True for the 12 built-in scenarios from the spec. */
  isCanonical?: boolean;
  /** Human-readable description of what this scenario demonstrates. */
  description?: string;
  /** Restrict BFS to these domain IDs (undefined = all domains). */
  domainFilter?: string[];
}

/** @deprecated Alias for ScenarioAction — use ScenarioAction in new code. */
export type UserAction = ScenarioAction;

// ─── v1 simulation step + trace (unchanged) ───────────────────────────────────

export interface SimulationStep {
  hop: number;
  actor: 'user' | 'cf' | 'event-bus';
  /** Cloud Function or event name; 'create' / 'update' for user steps. */
  source: string;
  writes: string[];
  cfsInvoked: string[];
  eventsFired: string[];
}

export interface SimulationTrace {
  action: ScenarioAction;
  steps: SimulationStep[];
}

// ─── v2 enriched step + trace ─────────────────────────────────────────────────

/**
 * EnrichedSimulationStep adds a 4th actor lane ('cf-callable') plus latency,
 * CF kind, and agent gate results. Intentionally not extending SimulationStep
 * to avoid actor union narrowing conflicts.
 */
export interface EnrichedSimulationStep {
  hop: number;
  actor: 'user' | 'cf' | 'event-bus' | 'cf-callable';
  source: string;
  writes: string[];
  cfsInvoked: string[];
  eventsFired: string[];
  /** CF execution kind, undefined for user/event-bus steps. */
  cfKind?: CfKind;
  /** Estimated latency for this step. */
  latency?: LatencyBand;
  /** Running total of p95 latency up to and including this step (ms). */
  cumulativeLatencyMs: number;
  /** Agent gate result; undefined if autonomyRequired === 0. */
  agentGate?: AgentGateResult;
  /** True if this step writes at least one sandbox-only entity (overlay-only). */
  fromSandboxOverlay?: boolean;
}

/** Point-in-time coverage snapshot across all accumulated traces. */
export interface CoverageSnapshot {
  entityHits: Array<{ entityId: string; count: number }>;
  cfHits: Array<{ cfName: string; count: number }>;
  domainCoverage: Array<{
    domainId: string;
    percent: number;
    hitCount: number;
    totalCount: number;
  }>;
  /** Entity IDs never touched by any accumulated trace. */
  darkZones: string[];
  /** Fraction of manifest entities touched at least once, 0–1. */
  totalEntityCoverage: number;
}

export interface EnrichedSimulationTrace {
  action: ScenarioAction;
  steps: EnrichedSimulationStep[];
  coverage: CoverageSnapshot;
  /** Sum of per-step p95 latency (ms). */
  totalLatencyMs: number;
  /** Worst agent gate tier across all steps. */
  safetyTier: SafetyTier;
}

// ─── scenario store types ─────────────────────────────────────────────────────

/** A user-authored scenario persisted in localStorage. */
export interface SavedScenario {
  id: string;
  name: string;
  action: ScenarioAction;
  createdAt: number;
  updatedAt: number;
}

// ─── domain event registry types ─────────────────────────────────────────────

export interface DomainEventEntry {
  type: string;
  domain: string;
  carriedEntities: string[];
  description?: string;
}

export const CANONICAL_ACTIONS: readonly UserAction[] = [
  {
    id: 'create-goal',
    label: 'User creates a Goal',
    writes: ['Goal'],
    fires: ['goal_created'],
  },
  {
    id: 'link-account',
    label: 'User links an Account',
    writes: ['Account', 'PlaidConnection'],
    fires: ['account_linked'],
  },
  {
    id: 'add-transaction',
    label: 'User adds a manual Transaction',
    writes: ['Transaction'],
    fires: ['transaction_created'],
  },
  {
    id: 'mark-bill-paid',
    label: 'User marks a Bill paid',
    writes: ['Bill'],
    fires: ['bill_paid'],
  },
  {
    id: 'create-budget',
    label: 'User creates a Budget',
    writes: ['Budget'],
    fires: ['budget_created'],
  },
];

const MAX_HOPS = 50;

/** Walk the manifest, producing an ordered list of simulation steps. */
export function simulate(
  manifest: AtlasManifest,
  action: UserAction,
  overlayManifest?: AtlasManifest,
): SimulationTrace {
  // Use overlayManifest if provided, otherwise use manifest
  const workingManifest = overlayManifest ?? manifest;

  const cfsByReadEntity = new Map<string, string[]>();
  // Only event-driven CF kinds get auto-invoked; 'scheduled' and 'http' don't.
  for (const cf of workingManifest.cloudFunctions) {
    if (cf.kind !== 'trigger' && cf.kind !== 'callable') continue;
    for (const e of cf.reads) {
      const list = cfsByReadEntity.get(e) ?? [];
      list.push(cf.name);
      cfsByReadEntity.set(e, list);
    }
  }
  const cfByName = new Map(workingManifest.cloudFunctions.map((cf) => [cf.name, cf]));

  const steps: SimulationStep[] = [];
  let hop = 0;

  // Hop 0 — the user's direct write + fired events.
  steps.push({
    hop: hop++,
    actor: 'user',
    source: action.label,
    writes: [...action.writes],
    cfsInvoked: [],
    eventsFired: [...action.fires],
  });

  // BFS over (writtenEntity, firedEvent) → triggered CFs → next-hop writes.
  const visitedCfWrite = new Set<string>(); // key = `cfName:entityId`
  const queue: string[] = [...action.writes];

  while (queue.length > 0 && hop < MAX_HOPS) {
    const next = queue.shift()!;
    const cfs = cfsByReadEntity.get(next) ?? [];
    for (const cfName of cfs) {
      const key = `${cfName}:${next}`;
      if (visitedCfWrite.has(key)) continue;
      visitedCfWrite.add(key);

      const cf = cfByName.get(cfName);
      if (!cf) continue;

      // The CF runs; its writes propagate.
      const events = (cf.writes ?? [])
        .flatMap((entityId) =>
          workingManifest.events.filter((e) => e.carriedEntities.includes(entityId)).map((e) => e.type),
        );
      steps.push({
        hop: hop++,
        actor: 'cf',
        source: cf.name,
        writes: [...cf.writes],
        cfsInvoked: [cf.name],
        eventsFired: events,
      });
      for (const w of cf.writes) {
        if (!queue.includes(w)) queue.push(w);
      }
    }
  }

  return { action, steps };
}
